/**
 * Cluster types for unit-level clustering
 */

// ========================================
// Cluster Types
// ========================================

export enum ClusterType {
  MANUAL = 'manual',
  FILTER = 'filter',
}

/**
 * Unit summary as part of a cluster response
 */
export interface UnitInCluster {
  id: string;
  boxNumber?: string;
  referencePlus?: string;
  floor?: number;
  unitType?: string;
  unitSubtype?: string;
  grossSurface?: number;
  netSurface?: number;
  rentStatus?: string;
  saleStatus?: string;
  building?: {
    id: string;
    googlePlaceLongAddress?: string;
    company?: {
      id: number;
      name: string;
    };
  };
}

export interface Cluster {
  id: number;
  name: string;
  description?: string;
  type: ClusterType;
  unitFilterCriteria?: UnitFilterCriteria;
  sortOrder?: number;
  icon?: string;
  units?: UnitInCluster[];
  unitCount?: number;
  buildingCount?: number;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// Filter Operators (shared across filter types)
// ========================================

export enum FilterOperator {
  // String/General operators
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  IN = 'in',
  NOT_IN = 'not_in',

  // Numeric operators
  GREATER_THAN = 'greater_than',
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN = 'less_than',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
  BETWEEN = 'between',

  // Null/Empty operators
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',

  // Array operators
  ARRAY_CONTAINS = 'array_contains',
  ARRAY_CONTAINS_ANY = 'array_contains_any',
  ARRAY_CONTAINS_ALL = 'array_contains_all',
}

export enum LogicalOperator {
  AND = 'and',
  OR = 'or',
}

// ========================================
// Unit-Level Filter Criteria Types
// ========================================

export interface UnitFilterCondition {
  id?: string; // For frontend tracking
  field: string;
  operator: FilterOperator;
  value: string | number | boolean | string[] | number[] | null;
  valueEnd?: number | string; // For BETWEEN operator
}

export interface UnitFilterGroup {
  id?: string; // For frontend tracking
  logicalOperator: LogicalOperator;
  conditions: (UnitFilterCondition | UnitFilterGroup)[];
}

export interface UnitFilterCriteria {
  version: '1.0';
  root: UnitFilterGroup;
}

// Type guards for unit filters
export function isUnitFilterGroup(
  condition: UnitFilterCondition | UnitFilterGroup,
): condition is UnitFilterGroup {
  return 'logicalOperator' in condition && 'conditions' in condition;
}

export function isUnitFilterCondition(
  condition: UnitFilterCondition | UnitFilterGroup,
): condition is UnitFilterCondition {
  return 'field' in condition && 'operator' in condition;
}

// ========================================
// Filterable Field Configuration
// ========================================

export interface FilterableFieldConfig {
  field: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'enum' | 'date' | 'relation';
  operators: FilterOperator[];
  enumValues?: Array<{ key: string; label: string }>;
  relationEndpoint?: string;
}

// Same type for unit filterable fields
export type UnitFilterableFieldConfig = FilterableFieldConfig;

// ========================================
// API Response Types
// ========================================

/**
 * Unit filter preview response with per-building grouping
 */
export interface UnitFilterPreviewResponse {
  units: UnitInCluster[];
  totalCount: number;
  buildingSummary: Array<{
    buildingId: string;
    buildingAddress: string;
    matchingUnitCount: number;
    totalUnitCount: number;
  }>;
}

export interface UnitFilterValidationResult {
  isValid: boolean;
  errors?: string[];
  matchingUnitCount?: number;
}

export interface CreateClusterDto {
  name: string;
  description?: string;
  type?: ClusterType;
  unitFilterCriteria?: UnitFilterCriteria;
  sortOrder?: number;
  icon?: string;
  unitIds?: string[];
}

export interface UpdateClusterDto {
  name?: string;
  description?: string;
  unitFilterCriteria?: UnitFilterCriteria;
  sortOrder?: number;
  icon?: string;
}
